'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect } from 'react';
import AuthButton from './auth/AuthButton';

// TICKER FEATURE - EASILY REMOVABLE
// To remove this feature, simply comment out or delete the TICKER_ENABLED constant
const TICKER_ENABLED = false;

export default function GlobalHeader() {
  

  // TICKER FEATURE - EASILY REMOVABLE
  // To remove this feature, simply set TICKER_ENABLED to false or comment out this section
  const [showTicker, setShowTicker] = useState(false);
  const [isClient, setIsClient] = useState(false);
  // Auto-hide on scroll down, reveal on scroll up (mobile-friendly)
  const [isHidden, setIsHidden] = useState(false);

  // Ensure client-side only rendering
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- client-mount gate; one-time sync after hydration
    setIsClient(true);
  }, []);

  // Hide the header when scrolling down, reveal on scroll up. Uses an
  // accumulated-distance threshold so it toggles only after a bit of sustained
  // scroll (not on tiny jitters / a single pixel of movement).
  useEffect(() => {
    const THRESHOLD = 48; // px of sustained scroll before toggling
    let lastY = window.scrollY;
    let acc = 0;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY;
        lastY = y;
        // reset the accumulator whenever the scroll direction flips
        if ((delta > 0 && acc < 0) || (delta < 0 && acc > 0)) acc = 0;
        acc += delta;
        if (y < 64) {
          setIsHidden(false); // always show near the top
          acc = 0;
        } else if (acc > THRESHOLD) {
          setIsHidden(true); // sustained scroll down
          acc = 0;
        } else if (acc < -THRESHOLD) {
          setIsHidden(false); // sustained scroll up
          acc = 0;
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Ticker visibility control
  useEffect(() => {
    if (!TICKER_ENABLED || !isClient) return;

    const showTickerFor5Seconds = () => {
      setShowTicker(true);
      
      // Hide after 5 seconds
      setTimeout(() => {
        setShowTicker(false);
      }, 5000);
    };

    // Show ticker every 10 seconds (5 seconds visible, 5 seconds hidden)
    const interval = setInterval(() => {
      showTickerFor5Seconds();
    }, 10000);

    // Show ticker immediately on first load
    showTickerFor5Seconds();

    return () => clearInterval(interval);
  }, [isClient]);

  return (
    <header
      className={`sticky top-0 z-50 text-zinc-900 py-1.5 md:py-1 shadow-lg border-b border-zinc-200 bg-gradient-to-r from-white via-zinc-50 to-zinc-100 transition-transform duration-300 will-change-transform ${
        isHidden ? '-translate-y-full' : 'translate-y-0'
      }`}
    >
      {/* Minimal background */}
      <div className="absolute inset-0 bg-zinc-50" />

      <div className="container mx-auto px-4 md:px-6 flex flex-row items-center justify-between relative min-h-[2.75rem] md:h-8 header-container">
        <div className="relative group">
          <Link
            href="https://tools.ghackk.com"
            className="block hover:scale-105 transition-transform duration-300 ease-in-out"
          >
            <Image
              src="/Ghackk-Logo-Horizontal.webp"
              alt="Ghackk Technologies"
              width={350}
              height={100}
              className="h-7 md:h-10 w-auto max-w-[180px] md:max-w-none"
              priority
            />
          </Link>
          <div
            className="absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-zinc-400 to-zinc-700 transition-all duration-300 group-hover:w-full"
            style={{ width: '0%' }}
          />
        </div>

        {/* TICKER FEATURE - EASILY REMOVABLE */}
        {TICKER_ENABLED && isClient && (
          <>
            {/* Hidden indicator dot */}
            <div className={`absolute left-1/2 transform -translate-x-1/2 z-10 transition-all duration-300 ease-in-out ${
              showTicker 
                ? 'opacity-0 scale-0' 
                : 'opacity-60 scale-100'
            }`}>
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
            </div>
            
            {/* Main ticker */}
            <div className={`absolute left-1/2 transform -translate-x-1/2 z-10 transition-all duration-500 ease-in-out ${
              showTicker 
                ? 'translate-y-0 opacity-100' 
                : '-translate-y-full opacity-0'
            }`}>
              <Link
                href="/bihar/teachers-pool"
                className="block w-full h-full cursor-pointer"
              >
                <div className="inline-flex items-center space-x-2 px-3 py-1.5 md:px-4 md:py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 animate-pulse border border-emerald-400/30">
                  <span className="text-xs md:text-sm font-semibold whitespace-nowrap">
                    📊 Check Your Mutual Match
                  </span>
                  <span className="text-xs bg-white/20 px-2 py-0.5 md:py-1 rounded-full hidden sm:inline">
                    View Now →
                  </span>
                  <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded-full sm:hidden">
                    →
                  </span>
                </div>
              </Link>
            </div>
          </>
        )}

        <div className="flex items-center space-x-2 md:space-x-4">
          {/* See Matches link (EN + HI) */}
          <a
            href="/dashboard?section=matches"
            className="group inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-zinc-100 transition-colors text-zinc-700"
            title="See your matches / अपने मैच देखें"
          >
            <span className="flex flex-col text-left leading-tight">
              <span className="text-[11px] font-semibold text-zinc-900">See Matches</span>
              <span className="text-[9px] text-zinc-500 mt-0.5">मैच देखें</span>
            </span>
            <svg className="w-3.5 h-3.5 text-zinc-500 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </a>

          <div className="transition-all duration-300 ease-in-out">
            <AuthButton />
          </div>
        </div>
      </div>
    </header>
  );
}

